import { InterventionCreationAction, InterventionCreationState } from './types';

export const initialInterventionCreationState: InterventionCreationState = {
  clientId: null,
  clientLabel: null,
  currentStep: 'requests',
  selectedRequestIds: [],
  newRequestIds: [],
  lastImportReport: null,
  lastDeletionReport: null,
  lastInterventionTypeReport: null,
  lastSendToAssignmentReport: null
};

export function interventionCreationReducer(
  state: InterventionCreationState,
  action: InterventionCreationAction
): InterventionCreationState {
  switch (action.type) {
    case 'selectClient':
      // Changer de client invalide tout ce qui a été fait pour le précédent :
      // on revient à la première étape et on vide la sélection.
      if (action.clientId === state.clientId) {
        return state;
      }
      return {
        ...state,
        clientId: action.clientId,
        clientLabel: action.clientLabel,
        currentStep: 'requests',
        selectedRequestIds: [],
        // Les demandes marquées « Nouveau » sont celles de l'ancien client, et la liste affichée
        // change entièrement : seul le changement de client efface ce marquage.
        newRequestIds: [],
        // Les bilans portent sur l'ancien client : ils n'ont plus de sens ici.
        lastImportReport: null,
        lastDeletionReport: null,
        lastInterventionTypeReport: null,
        lastSendToAssignmentReport: null
      };

    case 'goToStep':
      if (action.step === state.currentStep) {
        return state;
      }
      // Les deux étapes listent des ensembles disjoints : une sélection reportée
      // désignerait des lignes absentes de la liste affichée, et fausserait à la fois
      // les compteurs et la case à cocher maître.
      //
      // Les bilans partent avec elle : un bandeau rend compte d'un geste fait sur une
      // étape, le retrouver intact après un aller-retour le fait passer pour un
      // événement qui vient d'avoir lieu. Le marquage « Nouveau » survit, lui : il dit
      // l'état des lignes, pas un geste, et tient jusqu'au prochain ajout.
      return {
        ...state,
        currentStep: action.step,
        selectedRequestIds: [],
        lastImportReport: null,
        lastDeletionReport: null,
        lastInterventionTypeReport: null,
        lastSendToAssignmentReport: null
      };

    case 'toggleRequest': {
      const isSelected = state.selectedRequestIds.includes(action.id);
      return {
        ...state,
        selectedRequestIds: isSelected
          ? state.selectedRequestIds.filter((id) => id !== action.id)
          : [...state.selectedRequestIds, action.id]
      };
    }

    case 'setSelectedRequests':
      if (
        action.ids.length === state.selectedRequestIds.length &&
        action.ids.every((id, index) => id === state.selectedRequestIds[index])
      ) {
        return state;
      }
      return { ...state, selectedRequestIds: action.ids };

    case 'setNewRequests':
      return { ...state, newRequestIds: action.ids };

    case 'addNewRequest':
      // La saisie unitaire peut être rejouée sur le même équipement sans qu'il soit recréé :
      // le marquage ne doit pas accumuler deux fois le même identifiant.
      if (state.newRequestIds.includes(action.id)) {
        return state;
      }
      return { ...state, newRequestIds: [...state.newRequestIds, action.id] };

    case 'reportImport':
      return { ...state, lastImportReport: action.report };

    case 'reportDeletion':
      return { ...state, lastDeletionReport: action.report };

    case 'dismissDeletionReport':
      if (state.lastDeletionReport === null) {
        return state;
      }
      return { ...state, lastDeletionReport: null };

    case 'reportInterventionType':
      return { ...state, lastInterventionTypeReport: action.report };

    case 'dismissInterventionTypeReport':
      if (state.lastInterventionTypeReport === null) {
        return state;
      }
      return { ...state, lastInterventionTypeReport: null };

    /*
     * Alimenté par le bouton d'envoi et par une qualification réussie : même
     * aboutissement, donc un seul bandeau, et le dernier geste écrase le précédent.
     *
     * Effacé par `goToStep` comme les autres. Conséquence assumée : qualifier les
     * dernières demandes bascule automatiquement vers l'affectation et emporte le bilan,
     * ce cas précis n'affiche donc aucun bandeau — l'arrivée sur l'étape suivante fait
     * elle-même office de confirmation.
     */
    case 'reportSendToAssignment':
      return { ...state, lastSendToAssignmentReport: action.report };

    case 'dismissSendToAssignmentReport':
      if (state.lastSendToAssignmentReport === null) {
        return state;
      }
      return { ...state, lastSendToAssignmentReport: null };

    case 'dismissImportReport':
      if (state.lastImportReport === null) {
        return state;
      }
      return { ...state, lastImportReport: null };

    default:
      return state;
  }
}
